import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { List, MagnifyingGlass, Bell, User, Buildings } from "@phosphor-icons/react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { ROLE_LABEL } from "../../lib/api";
import { Link } from "react-router-dom";

export default function Topbar({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const { companies, activeCompanyId, setActive } = useCompany();

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-border" data-testid="topbar">
      <div className="flex items-center gap-3 px-4 lg:px-6 py-3">
        <button onClick={onToggleSidebar} className="lg:hidden p-1.5 hover:bg-[#EFF6FF] rounded-sm" data-testid="sidebar-toggle">
          <List size={18} />
        </button>

        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-border rounded-sm w-72 bg-[#F1F5F9]">
          <MagnifyingGlass size={14} className="text-[#64748B]" />
          <input
            placeholder="Search employees, candidates..."
            className="bg-transparent text-sm outline-none flex-1"
            data-testid="global-search"
          />
        </div>

        <div className="ml-auto flex items-center gap-3">
          {companies.length > 0 && (
            <div className="flex items-center gap-2 border-l border-border pl-3">
              <Buildings size={16} className="text-[#475569]" />
              <Select value={activeCompanyId || ""} onValueChange={setActive}>
                <SelectTrigger className="h-9 w-48 rounded-sm border-border text-xs uppercase tracking-wider" data-testid="company-selector">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.code}`}>
                      <span className="font-mono text-[10px] text-[#64748B] mr-2">{c.code}</span>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <button className="p-2 hover:bg-[#EFF6FF] rounded-sm" data-testid="notifications-button">
            <Bell size={16} />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-1 hover:bg-[#EFF6FF] rounded-sm" data-testid="user-menu">
                <Avatar className="h-8 w-8 rounded-sm">
                  <AvatarFallback className="bg-[#2563EB] text-white text-xs rounded-sm">{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <div className="text-xs font-medium">{user?.name}</div>
                  <div className="text-[10px] text-[#64748B] uppercase tracking-wider">
                    {ROLE_LABEL[user?.role] || user?.role}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/change-password" data-testid="change-password-link">Change Password</Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="dropdown-logout">Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
